import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Typography, IconButton, Card, CardContent,
  Button, ToggleButton, ToggleButtonGroup, Chip, Slider
} from "@mui/material";
import {
  ChevronLeft, ChevronRight, MusicNote,
  RecordVoiceOver, Map, ViewCarousel
} from "@mui/icons-material";

// ── ALPHARIDDIMS SCHEME ───────────────────────────────────────────────
// ARPAbet stress → Alphariddims encoding:
//   unstressed (0)       → 3 = bass    (low conga)    — X
//   secondary stress (2) → 1 = tone    (medium conga) — ▬
//   primary stress (1)   → 2 = slap    (high conga)   — ●
//   no vowel             → 0 = silence
const DRUM_SOUNDS = {
  3: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_low.mp3",
  2: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_high.mp3",
  1: "https://raw.githubusercontent.com/urbangriot/Drive/refs/heads/main/griotquest/audio/Conga/SO_AFR_conga_slap_closed.mp3",
  0: null,
};

const DRUM_SYMBOL = { 3: "X", 2: "●", 1: "▬", 0: "—" };
const DRUM_COLOR  = { 3: "#FDB93D", 2: "#F32718", 1: "#0D8FC7", 0: "#555555" };
const DRUM_LABEL  = { 3: "bass", 2: "tone", 1: "slap", 0: "silence" };
const DRUM_KEYS   = [3, 2, 1]; // bass · tone · slap (display order)

// ── PROVERBS ─────────────────────────────────────────────────────────
// Encoding: array-of-arrays — one sub-array per word, one value per syllable
// × = bass (3) — function words & unstressed syllables
// — = slap (2) — content words & primary stress
// • = tone (1) — secondary stress only
const proverbs = [
  { text: "rain beats a leopard's skin but it does not wash out the spots", country: "Ashanti", lesson: "encouragement", flag: "ashante", icon: "🌙", lat: 6.747, lng: -1.5209, animal: "leopard", rhythms: [[2],[2],[3],[2,3],[2],[2],[3],[3],[3],[2],[2],[3],[2]] },
  { text: "the moon moves slowly but it crosses the town", country: "Ashanti", lesson: "encouragement", flag: "ashante", icon: "🌙", lat: 6.747, lng: -1.5209, animal: "moon", rhythms: [[3],[2],[2],[2,3],[3],[3],[2,3],[3],[2]] },
  { text: "when spider webs unite they can tie up a lion", country: "Ethiopia", lesson: "teamwork", flag: "ethiopia", icon: "🐆", lat: 9.145, lng: 40.4897, animal: "spider", rhythms: [[3],[1,3],[2],[2,1],[3],[2],[1],[2],[2],[1,2]] },
  { text: "the cattle is as good as the pasture in which it grazes", country: "Ethiopia", lesson: "caution (food/environment/associations)", flag: "ethiopia", icon: "🐆", lat: 9.145, lng: 40.4897, animal: "cow", rhythms: [[3],[2,3],[3],[3],[2],[2],[2],[1,2],[3],[2],[2],[2,3]] },
  { text: "if one is not in a hurry even an egg will start walking", country: "Ethiopia", lesson: "caution", flag: "ethiopia", icon: "🥚", lat: 9.2, lng: 40.3, animal: "egg", rhythms: [[2],[2],[3],[3],[3],[3],[2,3],[2,3],[3],[2],[2],[2],[2,3]] },
  { text: "if there were no elephant in the jungle the buffalo would be great", country: "Ghana", lesson: "the second option is also good", flag: "ghana", icon: "🐘", lat: 7.9465, lng: -1.0232, animal: "elephant", rhythms: [[2],[2],[3],[3],[2,3,3],[3],[3],[2,3],[3],[2,3,1],[2],[3],[2]] },
  { text: "a crab does not beget a bird", country: "Ghana", lesson: "identity and sense of pride", flag: "ghana", icon: "🦀", lat: 7.9465, lng: -1.0232, animal: "crab", rhythms: [[3],[2],[3],[3],[3,2],[3],[2]] },
  { text: "one camel does not make fun of the other camel's lump", country: "Guinea", lesson: "respect others, don't make fun of others", flag: "guinea", icon: "🐪", lat: 9.9456, lng: -9.6966, animal: "camel", rhythms: [[2],[2,3],[3],[3],[2],[2],[3],[3],[2,3],[2,3],[2]] },
  { text: "cross the river in a crowd and the crocodile won't eat you", country: "Madagascar", lesson: "teamwork (apes strong together)", flag: "madagascar", icon: "🐊", lat: 18.7669, lng: 46.8691, animal: "crocodile", rhythms: [[2],[3],[2,3],[3],[3],[2],[3],[3],[2,3,1],[2],[2],[3]] },
  { text: "when the mouse laughs at the cat there is a hole nearby", country: "Nigeria", lesson: "confidence — have a backup plan", flag: "nigeria", icon: "🐭", lat: 9.082, lng: 8.6753, animal: "mouse", rhythms: [[2],[3],[2],[2],[3],[3],[2],[2],[3],[3],[2],[2,2]] },
  { text: "some birds avoid the water ducks seek it", country: "Nigeria", lesson: "individuality", flag: "nigeria", icon: "🦆", lat: 9.082, lng: 8.6753, animal: "duck", rhythms: [[2],[2],[3,2],[3],[2,3],[2],[2],[3]] },
  { text: "Earth is the queen of beds", country: "Nigeria", lesson: "Nature preservation", flag: "nigeria", icon: "🌍", lat: 9.082, lng: 8.6753, animal: "earth", rhythms: [[2],[3],[3],[2],[3],[2]] },
  { text: "if a centipede loses a leg it does not prevent him from walking", country: "Senegal", lesson: "small mistakes don't stop you from keep going", flag: "senegal", icon: "🐛", lat: 14.4974, lng: -14.4524, animal: "centipede", rhythms: [[2],[3],[2,3,1],[2,3],[3],[2],[3],[3],[3],[3,2],[2],[2],[2,3]] },
  { text: "however full the house the hen finds a corner to lay in", country: "Sierra Leone", lesson: "determination", flag: "sierraleone", icon: "🐓", lat: 8.4606, lng: -11.7799, animal: "hen", rhythms: [[1,2,3],[2],[3],[2],[3],[2],[2],[3],[2,3],[3],[2],[3]] },
  { text: "only a monkey understands a monkey", country: "Sierra Leone", lesson: "takes one to know one", flag: "sierraleone", icon: "🐒", lat: 8.4606, lng: -11.7799, animal: "monkey", rhythms: [[2,3],[3],[2,3],[1,3,2],[3],[2,3]] },
  { text: "a cow must graze where she is tied", country: "Sierra Leone", lesson: "acceptance/contentment", flag: "sierraleone", icon: "🐄", lat: 8.4606, lng: -11.7799, animal: "cow", rhythms: [[3],[2],[2],[2],[2],[3],[3],[2]] },
  { text: "it is the wondering dog that finds the old bone", country: "Sierra Leone", lesson: "perseverance", flag: "sierraleone", icon: "🐕", lat: 8.4606, lng: -11.7799, animal: "dog", rhythms: [[3],[3],[3],[2,3,3],[2],[2],[2],[3],[2],[2]] },
  { text: "a termite can do nothing to a stone save lick it", country: "Sudan", lesson: "resilience (ignore the haters)", flag: "sudan", icon: "🪨", lat: 12.8628, lng: 30.2176, animal: "stone", rhythms: [[3],[2,3],[2],[3],[2,3],[3],[3],[2],[2],[2],[3]] },
  { text: "a little shrub may grow into a tree", country: "Sudan", lesson: "patience", flag: "sudan", icon: "🌱", lat: 12.8628, lng: 30.2176, animal: "shrub", rhythms: [[3],[2,3],[2],[2],[2],[2,3],[3],[2]] },
  { text: "a roaring lion kills no game", country: "Uganda", lesson: "actions speak louder than words", flag: "uganda", icon: "🦁", lat: 1.3733, lng: 32.2903, animal: "lion", rhythms: [[3],[2,3],[2,3],[2],[3],[2]] },
  { text: "even an ant may harm an elephant", country: "Zululand", lesson: "humbleness", flag: "southafrica", icon: "🐜", lat: 27.8872, lng: 31.4456, animal: "ant", rhythms: [[2,3],[3],[2],[2],[2],[3],[2,3,3]] },
  { text: "the horse who arrives early gets good drinking water", country: "Zululand", lesson: "early bird gets the worm", flag: "southafrica", icon: "🐴", lat: 27.8872, lng: 31.4456, animal: "horse", rhythms: [[3],[2],[2],[3,2],[2,3],[2],[2],[2,3],[2,3]] },
  { text: "when the man is away the monkey eats up the maize and enters the hut", country: "Zululand", lesson: "caution", flag: "southafrica", icon: "🐒", lat: 27.8872, lng: 31.4456, animal: "monkey", rhythms: [[2],[3],[2],[3],[3,2],[3],[2,3],[2],[2],[3],[2],[3],[2,3],[3],[2]] },
  { text: "one does not cross a river without getting wet", country: "Zululand", lesson: "fortitude, courage", flag: "southafrica", icon: "🌊", lat: 27.8872, lng: 31.4456, animal: "puppy", rhythms: [[2],[3],[3],[2],[3],[2,3],[3,2],[2,3],[2]] },
  { text: "a horse has four legs yet it often falls", country: "Zululand", lesson: "persistence", flag: "southafrica", icon: "🐎", lat: 27.8872, lng: 31.4456, animal: "horse", rhythms: [[3],[2],[2],[2],[2],[3],[3],[2,3,2],[3]] },
  { text: "if you do not step on a dog's tail he will not bite you", country: "Cameroon", lesson: "cautionary", flag: "cameroon", icon: "🐒", lat: 7.3697, lng: 12.3547, animal: "dog", rhythms: [[2],[3],[3],[3],[2],[3],[3],[2],[2],[3],[2],[3],[2],[3]] },
  { text: "by trying often the monkey learns to jump from the tree", country: "Cameroon", lesson: "practice makes perfect — persistence", flag: "cameroon", icon: "🌳", lat: 7.3697, lng: 12.3547, animal: "monkey", rhythms: [[3],[2,3],[2,3],[3],[2,3],[2],[3],[2],[2],[3],[2]] },
  { text: "the cricket cries the year changes", country: "Cameroon", lesson: "life goes on", flag: "cameroon", icon: "🦗", lat: 7.3697, lng: 12.3547, animal: "cricket", rhythms: [[3],[2,3],[2],[3],[2],[2,3]] },
  { text: "an elephant will reach to the roof of the house", country: "Cameroon", lesson: "there is always something bigger", flag: "cameroon", icon: "🐘", lat: 7.3697, lng: 12.3547, animal: "elephant", rhythms: [[3],[2,3,3],[2],[2],[3],[3],[2],[3],[3],[2]] },
  { text: "two birds disputed about a kernel when a third swooped down and carried it off", country: "Congo", lesson: "arguing wastes time — someone else takes the prize", flag: "congo", icon: "🐦", lat: 4.0383, lng: 21.7587, animal: "crow", rhythms: [[2],[2],[3,2,3],[3,2],[3],[2,3],[2],[3],[2],[2],[2],[3],[2,3],[3],[2]] },
]

// ── FLAGS ─────────────────────────────────────────────────────────────
const FLAG_COLORS = {
  ashante:     ["#006B3F","#FCD116","#CE1126"],
  ethiopia:    ["#078930","#FCDD09","#DA121A"],
  ghana:       ["#006B3F","#FCD116","#CE1126"],
  guinea:      ["#CE1126","#FCD116","#009460"],
  madagascar:  ["#FC3D32","#FC3D32","#007E3A"],
  nigeria:     ["#008751","#FFFFFF","#008751"],
  senegal:     ["#00853F","#FDEF42","#E31B23"],
  sierraleone: ["#1EB53A","#FFFFFF","#0072C6"],
  sudan:       ["#D21034","#FFFFFF","#000000"],
  uganda:      ["#000000","#FCDC04","#DE3108"],
  southafrica: ["#007A4D","#FFB612","#DE3831"],
  cameroon:    ["#007A5E","#CE1126","#FCD116"],
  congo:       ["#007FFF","#FBDE4A","#CE1126"],
};

const FLAG_STAR = ["ethiopia","ghana","senegal","cameroon","guinea"];

const FlagComponent = ({ country }) => {
  const colors = FLAG_COLORS[country];
  if (!colors) return null;
  return (
    <Box sx={{ position: "relative", width: 36, height: 24, display: "flex", flexDirection: "column", borderRadius: "2px", overflow: "hidden" }}>
      {colors.map((c, i) => <Box key={i} sx={{ flex: 1, bgcolor: c }} />)}
      {FLAG_STAR.includes(country) && (
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: country === "ethiopia" ? "#0F47AF" : "white", fontSize: "13px", lineHeight: 1 }}>★</Box>
      )}
    </Box>
  );
};

// ── PROVERB CARD ──────────────────────────────────────────────────────
const ProverbCard = ({ proverb, onPlay, onRead, isPlaying, isReading, highlightedWordIndex, highlightedRhythmIndex, onWordClick, compact = false, speed = 2, onSpeedChange }) => {
  const words = proverb.text.split(" ");
  return (
    <Card sx={{ backgroundColor: "#4aabc5", color: "white", borderRadius: compact ? "12px" : "16px", overflow: "hidden", width: "100%" }}>
      <CardContent sx={{ padding: compact ? "14px" : "24px", "&:last-child": { pb: compact ? "14px" : "24px" } }}>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: compact ? 1 : 2 }}>
          <Typography variant={compact ? "body2" : "h5"} sx={{ fontWeight: "bold" }}>🌴 Proverbs Camp</Typography>
          <Box sx={{ backgroundColor: "rgba(255,255,255,0.2)", borderRadius: "50px", padding: compact ? "4px 8px" : "6px 12px", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FlagComponent country={proverb.flag} />
            <Typography variant="caption" sx={{ mt: 0.5, fontWeight: "bold", fontSize: compact ? "9px" : "11px" }}>{proverb.country}</Typography>
          </Box>
        </Box>

        {proverb.lesson && (
          <Typography variant="caption" sx={{ display: "block", mb: 1, opacity: 0.8, fontStyle: "italic", fontSize: compact ? "10px" : "12px" }}>
            💡 {proverb.lesson}
          </Typography>
        )}

        {/* Words with drum symbols — rhythms[i] is array of syllable values */}
        <Box sx={{ textAlign: "center", mb: compact ? 1 : 3 }}>
          {words.map((word, index) => {
            const syllables = proverb.rhythms[index] || [];
            const isActive = highlightedWordIndex === index;
            // dominant color = first primary (2) or first value
            const dominant = syllables.includes(2) ? 2 : syllables.includes(1) ? 1 : syllables[0] ?? 3;
            return (
              <Box key={index} sx={{ display: "inline-block", margin: compact ? "0 2px 8px" : "0 3px 12px" }}>
                <Typography
                  component="span"
                  onClick={() => onWordClick && onWordClick(index)}
                  sx={{
                    cursor: "pointer", transition: "all 0.15s", display: "block",
                    color: isActive ? DRUM_COLOR[dominant] : "white",
                    textShadow: isActive ? `0 0 10px ${DRUM_COLOR[dominant]}` : "none",
                    transform: isActive ? "scale(1.12)" : "scale(1)",
                    "&:hover": { opacity: 0.8 },
                    fontSize: compact ? { xs: "13px", sm: "15px" } : { xs: "18px", sm: "26px" },
                    fontWeight: isActive ? 700 : 400,
                    lineHeight: compact ? 1.4 : 1.5,
                  }}
                >
                  {word}
                </Typography>
                {syllables.length > 0 && (
                  <Box sx={{
                    display: "flex", justifyContent: "center", gap: "2px",
                    backgroundColor: highlightedRhythmIndex === index ? DRUM_COLOR[dominant] : "rgba(0,0,0,0.2)",
                    borderRadius: "20px",
                    padding: compact ? "1px 5px" : "3px 8px",
                    marginTop: "2px", transition: "background 0.15s",
                  }}>
                    {syllables.map((s, si) => (
                      <Typography key={si} sx={{
                        color: "white", fontWeight: "bold",
                        fontSize: compact ? "11px" : { xs: "14px", sm: "17px" },
                        lineHeight: 1,
                      }}>
                        {DRUM_SYMBOL[s]}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Legend */}
        {!compact && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
            {DRUM_KEYS.map((k) => (
              <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: DRUM_COLOR[k], display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: "white", fontSize: "10px", fontWeight: 700 }}>{DRUM_SYMBOL[k]}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: "10px" }}>{DRUM_LABEL[k]}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
          <Button variant="contained" startIcon={<MusicNote sx={{ fontSize: compact ? 13 : 18 }} />}
            onClick={onPlay} disabled={isPlaying || isReading} size={compact ? "small" : "medium"}
            sx={{ backgroundColor: "#FC5E0D", color: "white", "&:hover": { backgroundColor: "#e6540b" }, "&:disabled": { opacity: 0.5, color: "white" }, fontWeight: "bold", borderRadius: "8px", fontSize: compact ? "0.65rem" : undefined }}>
            {isPlaying ? "Playing..." : "Play"}
          </Button>
          <Button variant="contained" startIcon={<RecordVoiceOver sx={{ fontSize: compact ? 13 : 18 }} />}
            onClick={onRead} disabled={isReading || isPlaying} size={compact ? "small" : "medium"}
            sx={{ backgroundColor: "#FC5E0D", color: "white", "&:hover": { backgroundColor: "#e6540b" }, "&:disabled": { opacity: 0.5, color: "white" }, fontWeight: "bold", borderRadius: "8px", fontSize: compact ? "0.65rem" : undefined }}>
            {isReading ? "Reading..." : "Read"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── MAP VIEW ──────────────────────────────────────────────────────────
const MapView = ({ proverbs, onPlay, onRead, isPlaying, isReading, highlightedWordIndex, highlightedRhythmIndex, onWordClick, countryFilter }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [activeProverb, setActiveProverb] = useState(null);
  const [popupPos, setPopupPos] = useState(null);

  const filtered = countryFilter ? proverbs.filter(p => p.country === countryFilter) : proverbs;

  useEffect(() => {
    if (window.L) { setLeafletLoaded(true); return; }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload = () => setLeafletLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { center: [5, 20], zoom: 3, scrollWheelZoom: false });
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=cb1_42ii_1_2cf9e03592ae4317b455a173", {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a>', maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    map.on("click", () => { setActiveProverb(null); setPopupPos(null); });
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [leafletLoaded]);

  useEffect(() => {
    if (!mapInstanceRef.current || !leafletLoaded) return;
    const L = window.L;
    const map = mapInstanceRef.current;
    map.eachLayer(layer => { if (layer instanceof L.Marker) map.removeLayer(layer); });
    filtered.forEach((p) => {
      const icon = L.divIcon({
        html: `<div style="width:40px;height:40px;border-radius:50%;background:#4aabc5;border:2.5px solid white;display:flex;align-items:center;justify-content:center;font-size:18px;cursor:pointer;box-shadow:0 0 0 3px rgba(74,171,197,0.3),0 4px 10px rgba(0,0,0,0.5)">${p.icon}</div>`,
        className: "", iconSize: [40, 40], iconAnchor: [20, 40],
      });
      const marker = L.marker([p.lat, p.lng], { icon }).addTo(map);
      marker.on("click", (e) => {
        const pt = map.latLngToContainerPoint(e.latlng);
        setActiveProverb(p);
        setPopupPos({ x: pt.x, y: pt.y });
        e.originalEvent.stopPropagation();
      });
    });
  }, [leafletLoaded, filtered]);

  return (
    <Box sx={{ position: "relative", width: "100%", borderRadius: "16px", overflow: "hidden" }}>
      <Box ref={mapRef} sx={{ width: "100%", height: { xs: "440px", sm: "520px" }, border: "2px solid #333", borderRadius: "16px" }} />

      {/* Legend */}
      <Box sx={{ position: "absolute", bottom: 16, left: 16, zIndex: 1000, backgroundColor: "rgba(17,17,17,0.88)", backdropFilter: "blur(6px)", borderRadius: "10px", padding: "10px 14px", border: "1px solid rgba(255,255,255,0.1)", maxHeight: "200px", overflowY: "auto" }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mb: 0.75, letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "9px" }}>
          {filtered.length} proverb{filtered.length !== 1 ? "s" : ""}
        </Typography>
        {[...new Set(filtered.map(p => p.country))].sort().map(country => {
          const p = filtered.find(x => x.country === country);
          const count = filtered.filter(x => x.country === country).length;
          return (
            <Box key={country} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
              <Typography sx={{ fontSize: "13px" }}>{p.icon}</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", fontSize: "11px" }}>{country}</Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontSize: "10px" }}>×{count}</Typography>
            </Box>
          );
        })}
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.25)", display: "block", mt: 0.5, fontSize: "9px" }}>tap a marker to explore</Typography>
      </Box>

      {/* Popup */}
      {activeProverb && popupPos && (
        <Box sx={{
          position: "absolute",
          left: Math.min(Math.max(popupPos.x - 160, 8), (mapRef.current?.offsetWidth || 400) - 328),
          top: Math.max(popupPos.y - 340, 8),
          width: 320, zIndex: 2000,
          animation: "popIn 0.18s ease",
          "@keyframes popIn": { from: { opacity: 0, transform: "scale(0.95) translateY(6px)" }, to: { opacity: 1, transform: "scale(1) translateY(0)" } },
        }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <IconButton size="small" onClick={() => { setActiveProverb(null); setPopupPos(null); }}
              sx={{ bgcolor: "rgba(0,0,0,0.7)", color: "white", width: 22, height: 22, fontSize: "13px", "&:hover": { bgcolor: "rgba(0,0,0,0.9)" } }}>×</IconButton>
          </Box>
          <ProverbCard proverb={activeProverb}
            onPlay={() => onPlay(activeProverb)} onRead={() => onRead(activeProverb)}
            isPlaying={isPlaying} isReading={isReading}
            highlightedWordIndex={highlightedWordIndex} highlightedRhythmIndex={highlightedRhythmIndex}
            onWordClick={(i) => onWordClick(i, activeProverb)} compact />
          <Box sx={{ width: 0, height: 0, borderLeft: "8px solid transparent", borderRight: "8px solid transparent", borderTop: "8px solid #4aabc5", mx: "auto" }} />
        </Box>
      )}
    </Box>
  );
};

// ── MAIN ──────────────────────────────────────────────────────────────
const DrumTalk = () => {
  const [view, setView] = useState("carousel");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [countryFilter, setCountryFilter] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [speed, setSpeed] = useState(2); // 1=Slow 2=Normal 3=Fast
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const [highlightedRhythmIndex, setHighlightedRhythmIndex] = useState(-1);

  const audioContextRef = useRef(null);
  const bufferCache = useRef({});
  const touchStartX = useRef(null);

  const filtered = countryFilter ? proverbs.filter(p => p.country === countryFilter) : proverbs;
  const currentProverb = filtered[currentIndex] || filtered[0];
  const countries = [...new Set(proverbs.map(p => p.country))].sort();

  useEffect(() => {
    window.speechSynthesis.getVoices();
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => { window.speechSynthesis.cancel(); audioContextRef.current?.close(); };
  }, []);

  // ── fetchBuffer ───────────────────────────────────────────────────
  const fetchBuffer = useCallback(async (url) => {
    if (bufferCache.current[url]) return bufferCache.current[url];
    const res = await fetch(url);
    const ab = await res.arrayBuffer();
    const buf = await audioContextRef.current.decodeAudioData(ab);
    bufferCache.current[url] = buf;
    return buf;
  }, []);

  // ── playRhythm — plays array of syllable values for one word ───────
const playRhythm = useCallback(async (syllables, index) => {
  if (!syllables?.length || !audioContextRef.current) return 0;
  if (audioContextRef.current.state === "suspended") {
    await audioContextRef.current.resume();
  }
  setHighlightedRhythmIndex(index);
  const ctx = audioContextRef.current;

  // Speed multiplier: 1=Slow(300ms) 2=Normal(220ms) 3=Fast(140ms)
  const SYLLABLE_MS = speed === 1 ? 300 : speed === 3 ? 140 : 220;

  for (let i = 0; i < syllables.length; i++) {
    const url = DRUM_SOUNDS[syllables[i]];
    if (url) {
      const buf = await fetchBuffer(url);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(ctx.currentTime);
    }
    if (i < syllables.length - 1) {
      await new Promise(r => setTimeout(r, SYLLABLE_MS));
    }
  }

  return syllables.length * SYLLABLE_MS;
}, [fetchBuffer, speed]);


  // ── playWordRhythm — tap a word ───────────────────────────────────
  const playWordRhythm = useCallback(async (index, proverb) => {
    const p = proverb || currentProverb;
    const syllables = p.rhythms[index];
    if (!syllables?.length) return;
    if (audioContextRef.current?.state === "suspended") await audioContextRef.current.resume();
    setHighlightedWordIndex(index);
    await playRhythm(syllables, index);
    const duration = syllables.length * 120 + 300;
    setTimeout(() => { setHighlightedWordIndex(-1); setHighlightedRhythmIndex(-1); }, duration);
  }, [currentProverb, playRhythm]);

  // ── playFullProverb ───────────────────────────────────────────────
const playFullProverb = useCallback(async (proverb) => {
  const p = proverb || currentProverb;
  if (audioContextRef.current?.state === "suspended") {
    await audioContextRef.current.resume();
  }
  setIsPlaying(true);

  // Speed multiplier: 1=Slow(280ms) 2=Normal(180ms) 3=Fast(90ms)
  const WORD_GAP_BASE = speed === 1 ? 280 : speed === 3 ? 90 : 180;
  // Extra time per syllable in the upcoming word (longer words = slightly more prep time)
  const SYLLABLE_WEIGHT = 30;

  for (let i = 0; i < p.rhythms.length; i++) {
    const syllables = Array.isArray(p.rhythms[i])
      ? p.rhythms[i]
      : p.rhythms[i] !== undefined ? [p.rhythms[i]] : [];

    setHighlightedWordIndex(i);
    await playRhythm(syllables, i);

    // Gap after word = base + extra for multi-syllable words
    const gap = WORD_GAP_BASE + (syllables.length - 1) * SYLLABLE_WEIGHT;
    await new Promise(r => setTimeout(r, gap));
  }

  setIsPlaying(false);
  setHighlightedWordIndex(-1);
  setHighlightedRhythmIndex(-1);
}, [currentProverb, playRhythm, speed]);

  // ── readProverb ───────────────────────────────────────────────────
  const readProverb = useCallback((proverb) => {
    if (isReading) return;
    window.speechSynthesis.cancel();
    const p = proverb || currentProverb;
    const utt = new SpeechSynthesisUtterance(p.text);
    utt.rate = 0.85; utt.pitch = 1.0; utt.volume = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find(v => v.name.includes("Samantha") || v.name.includes("Google US") || v.lang === "en-US");
    if (v) utt.voice = v;
    utt.onboundary = (e) => {
      if (e.name === "word") setHighlightedWordIndex(p.text.substring(0, e.charIndex).split(" ").length - 1);
    };
    utt.onstart = () => setIsReading(true);
    utt.onend = () => { setIsReading(false); setHighlightedWordIndex(-1); };
    utt.onerror = () => { setIsReading(false); setHighlightedWordIndex(-1); };
    setIsReading(true);
    window.speechSynthesis.speak(utt);
  }, [isReading, currentProverb]);

  const handlePrev = () => setCurrentIndex(i => (i === 0 ? filtered.length - 1 : i - 1));
  const handleNext = () => setCurrentIndex(i => (i === filtered.length - 1 ? 0 : i + 1));
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (!touchStartX.current) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? handleNext() : handlePrev(); }
    touchStartX.current = null;
  };
  const handleCountryFilter = (country) => {
    setCountryFilter(prev => prev === country ? null : country);
    setCurrentIndex(0);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Explore African proverbs with rhythm patterns. Tap a word to hear its drum sound.
      </Typography>

      {/* Drum key */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}>
        {DRUM_KEYS.map((k) => (
          <Box key={k} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: DRUM_COLOR[k], display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid white" }}>
              <Typography sx={{ color: "white", fontSize: "11px", fontWeight: 700 }}>{DRUM_SYMBOL[k]}</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em" }}>{DRUM_LABEL[k]}</Typography>
          </Box>
        ))}
      </Box>

      {/* View toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
        <ToggleButtonGroup value={view} exclusive onChange={(_, v) => { if (v) setView(v); }} size="small"
          sx={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px",
            "& .MuiToggleButton-root": { color: "rgba(255,255,255,0.5)", border: "none", borderRadius: "9px !important", px: 2.5, py: 0.75, gap: 0.75, fontSize: "13px", fontWeight: 500, textTransform: "none",
              "&.Mui-selected": { backgroundColor: "#4aabc5", color: "white" },
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" } } }}>
          <ToggleButton value="carousel"><ViewCarousel sx={{ fontSize: 16 }} /> Carousel</ToggleButton>
          <ToggleButton value="map"><Map sx={{ fontSize: 16 }} /> Map</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Country filter */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2.5, justifyContent: "center" }}>
        {countries.map(country => {
          const p = proverbs.find(x => x.country === country);
          const count = proverbs.filter(x => x.country === country).length;
          return (
            <Chip key={country} label={`${p.icon} ${country} (${count})`} size="small" onClick={() => handleCountryFilter(country)}
              sx={{ fontSize: "11px", cursor: "pointer", transition: "all 0.2s",
                backgroundColor: countryFilter === country ? "#4aabc5" : "rgba(255,255,255,0.08)",
                color: countryFilter === country ? "white" : "rgba(255,255,255,0.6)",
                border: countryFilter === country ? "1px solid #4aabc5" : "1px solid rgba(255,255,255,0.15)",
                "&:hover": { backgroundColor: countryFilter === country ? "#3d9ab3" : "rgba(255,255,255,0.15)" } }} />
          );
        })}
        {countryFilter && (
          <Chip label="× Clear" size="small" onClick={() => { setCountryFilter(null); setCurrentIndex(0); }}
            sx={{ fontSize: "11px", cursor: "pointer", backgroundColor: "rgba(252,94,13,0.2)", color: "#FC5E0D", border: "1px solid rgba(252,94,13,0.4)" }} />
        )}
      </Box>

      {/* Speed slider */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, px: 1, justifyContent: "center" }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>
          Speed
        </Typography>
        <Slider
          value={speed}
          min={1}
          max={3}
          step={1}
          marks={[
            { value: 1, label: "Slow" },
            { value: 2, label: "Normal" },
            { value: 3, label: "Fast" },
          ]}
          onChange={(_, val) => setSpeed(val)}
          sx={{
            flex: 1,
            maxWidth: 240,
            color: "#4aabc5",
            "& .MuiSlider-thumb": { width: 16, height: 16, bgcolor: "#4aabc5", "&:hover": { boxShadow: "0 0 0 8px rgba(74,171,197,0.2)" } },
            "& .MuiSlider-track": { bgcolor: "#4aabc5", border: "none" },
            "& .MuiSlider-rail": { bgcolor: "rgba(255,255,255,0.2)" },
            "& .MuiSlider-markLabel": { color: "rgba(255,255,255,0.55)", fontSize: "0.7rem" },
            "& .MuiSlider-mark": { bgcolor: "rgba(255,255,255,0.35)" },
          }}
        />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: "#4aabc5", minWidth: 52 }}>
          {{ 1: "Slow", 2: "Normal", 3: "Fast" }[speed]}
        </Typography>
      </Box>

      {/* Carousel */}
      {view === "carousel" && (
        <Box>
          {filtered.length === 0
            ? <Typography sx={{ textAlign: "center", color: "rgba(255,255,255,0.4)", py: 4 }}>No proverbs for this filter.</Typography>
            : <Box onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                <ProverbCard proverb={currentProverb}
                  onPlay={() => playFullProverb()} onRead={() => readProverb()}
                  isPlaying={isPlaying} isReading={isReading}
                  highlightedWordIndex={highlightedWordIndex} highlightedRhythmIndex={highlightedRhythmIndex}
                  onWordClick={(i) => playWordRhythm(i)}
                  speed={speed} onSpeedChange={setSpeed} />
              </Box>
          }
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
            <IconButton onClick={handlePrev} sx={{ color: "#00B2CA" }}><ChevronLeft /></IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", mr: 1 }}>{currentIndex + 1} / {filtered.length}</Typography>
              {filtered.slice(0, 10).map((_, i) => (
                <Box key={i} onClick={() => setCurrentIndex(i)} sx={{ width: i === currentIndex ? 18 : 7, height: 7, borderRadius: "4px", bgcolor: i === currentIndex ? "#4aabc5" : "rgba(255,255,255,0.2)", cursor: "pointer", transition: "all 0.25s" }} />
              ))}
              {filtered.length > 10 && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", ml: 0.5 }}>+{filtered.length - 10}</Typography>}
            </Box>
            <IconButton onClick={handleNext} sx={{ color: "#00B2CA" }}><ChevronRight /></IconButton>
          </Box>
        </Box>
      )}

      {/* Map */}
      {view === "map" && (
        <MapView proverbs={proverbs} countryFilter={countryFilter}
          onPlay={(p) => playFullProverb(p)} onRead={(p) => readProverb(p)}
          isPlaying={isPlaying} isReading={isReading}
          highlightedWordIndex={highlightedWordIndex} highlightedRhythmIndex={highlightedRhythmIndex}
          onWordClick={(i, p) => playWordRhythm(i, p)} speed={speed} />
      )}
    </Box>
  );
};

export default DrumTalk;